/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        MeteoDetailPanel.tsx
 * @description Panel detaille des conditions meteorologiques pour la regulation
 *              Affiche toutes les informations pertinentes pour les operations
 *              hivernales sur un aeroport
 *
 * @version     2.3
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useState } from 'react';
import { useMetarWinter, useRefreshMetar, getAlertColorClass } from '@/hooks/useMetar';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CloudIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

// Aeroports disponibles
const AIRPORTS = [
  { icao: 'LFPO', name: 'Paris-Orly', iata: 'ORY' },
  { icao: 'LFPG', name: 'Paris-CDG', iata: 'CDG' },
];

interface MeteoDetailPanelProps {
  icao?: string;
  className?: string;
  allowSwitch?: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

/**
 * Panel de details meteorologiques pour la page Regulation
 *
 * Affiche les donnees critiques pour les operations de deneigement:
 * - Temperature et point de rosee
 * - Ecart thermique (risque givrage)
 * - Vent et rafales
 * - Visibilite
 * - Risques hiver
 * - Recommandations operationnelles
 */
export function MeteoDetailPanel({ icao = 'LFPO', className, allowSwitch = true, collapsible = true, defaultCollapsed = false }: MeteoDetailPanelProps) {
  const [selectedIcao, setSelectedIcao] = useState(icao);
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const { data: winter, isLoading, isError, refetch } = useMetarWinter(selectedIcao);
  const refreshMetar = useRefreshMetar();

  const currentAirport = AIRPORTS.find(a => a.icao === selectedIcao) || AIRPORTS[0];

  const handleRefresh = async () => {
    await refreshMetar(selectedIcao);
    refetch();
  };

  if (isLoading) {
    return (
      <div className={clsx('card animate-pulse', className)}>
        <div className="card-header">
          <div className="h-5 w-40 bg-gray-200 rounded" />
        </div>
        <div className="card-body space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError || !winter) {
    return (
      <div className={clsx('card', className)}>
        <div className="card-body text-center py-8">
          <CloudIcon className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500 mb-3">Donnees meteo non disponibles</p>
          <button
            onClick={handleRefresh}
            className="btn-secondary inline-flex items-center"
          >
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Reessayer
          </button>
        </div>
      </div>
    );
  }

  const alertLevel = winter.alert.level;

  return (
    <div className={clsx('card overflow-hidden', className)}>
      {/* Header compact */}
      <div className={clsx(
        'px-3 py-2 flex items-center justify-between',
        alertLevel !== 'normal' ? getAlertColorClass(alertLevel) : 'bg-gray-50'
      )}>
        {/* Gauche: Onglets aéroport */}
        <div className="flex items-center gap-2">
          {allowSwitch ? (
            <div className="flex rounded overflow-hidden border border-gray-300 text-xs">
              {AIRPORTS.map((airport) => (
                <button
                  key={airport.icao}
                  onClick={() => setSelectedIcao(airport.icao)}
                  className={clsx(
                    'px-2 py-1 font-medium transition-colors',
                    airport.icao === selectedIcao
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  )}
                >
                  {airport.iata}
                </button>
              ))}
            </div>
          ) : (
            <span className="text-xs font-medium">{currentAirport.iata}</span>
          )}
          <span className="text-xs opacity-60">{winter.metar_tranche}</span>
        </div>

        {/* Droite: Actions */}
        <div className="flex items-center gap-1">
          {alertLevel !== 'normal' && (
            <ExclamationTriangleIcon className="w-4 h-4 animate-pulse" />
          )}
          <button onClick={handleRefresh} className="p-1 hover:bg-black/10 rounded" title="Rafraîchir">
            <ArrowPathIcon className="w-3.5 h-3.5" />
          </button>
          {collapsible && (
            <button onClick={() => setIsCollapsed(!isCollapsed)} className="p-1 hover:bg-black/10 rounded">
              {isCollapsed ? <ChevronDownIcon className="w-3.5 h-3.5" /> : <ChevronUpIcon className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>

      {/* Corps - une seule ligne */}
      {!isCollapsed && (
      <div className="px-3 py-2 flex items-center justify-between gap-2 text-xs bg-white">
        {/* Rosée + Écart */}
        <div className={clsx(
          'flex items-center gap-1 px-2 py-1 rounded',
          winter.spread_temp_dewpoint !== null && winter.spread_temp_dewpoint <= 3
            ? 'bg-cyan-100 text-cyan-800'
            : 'bg-gray-100 text-gray-600'
        )}>
          <span>Rosée {winter.dewpoint}°</span>
          <span className="font-bold">Δ{winter.spread_temp_dewpoint}°</span>
        </div>

        {/* Risques inline */}
        <div className="flex items-center gap-1">
          {winter.risques.givrage && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Giv</span>}
          {winter.risques.verglas && <span className="px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700">Verg</span>}
          {winter.risques.neige_active && <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">Neige</span>}
          {winter.operations.snoclo && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold animate-pulse">SNOCLO</span>}
          {!winter.risques.givrage && !winter.risques.verglas && !winter.risques.neige_active && !winter.operations.snoclo && (
            <span className="text-gray-400">Aucun risque</span>
          )}
        </div>

        {/* Statut */}
        <div className={clsx(
          'flex items-center gap-1 px-2 py-1 rounded font-medium',
          winter.operations.conditions_operables ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        )}>
          {winter.operations.conditions_operables ? <CheckCircleIcon className="w-3.5 h-3.5" /> : <ExclamationTriangleIcon className="w-3.5 h-3.5" />}
          <span className={clsx(
            'px-1 rounded',
            winter.recommended_priority === 'critique' && 'bg-red-200',
            winter.recommended_priority === 'urgente' && 'bg-amber-200',
            winter.recommended_priority === 'normale' && 'bg-green-200'
          )}>
            {winter.recommended_priority}
          </span>
        </div>
      </div>)}
    </div>
  );
}

export default MeteoDetailPanel;
